// /admin/cobros-pendientes — listado de pedidos pagados por transferencia bancaria
// que esperan confirmación manual de Roberto/admin.
//
// Cuando se aprieta "Confirmar pago", el pedido pasa a estado='pagado',
// se setea pagado_at y pagado_confirmado_por_id, y se dispara el trigger
// que genera CFE + email + WA automático.

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/AuthContext'

const fmt = (n) => '$' + Math.round(Number(n || 0)).toLocaleString('es-UY')

function fmtFecha(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('es-UY', { day: 'numeric', month: 'short', year: 'numeric' })
}

function fmtFechaHora(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleString('es-UY', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

export default function CobrosPendientesTransferencia() {
  const { user } = useAuth()
  const [pedidos, setPedidos] = useState([])
  const [loading, setLoading] = useState(true)
  const [confirmando, setConfirmando] = useState(null) // pedido_id en proceso
  const [comprobantePreview, setComprobantePreview] = useState(null) // { url, filename }
  const [filtro, setFiltro] = useState('todos') // todos | b2c | b2b
  const [mostrarPruebas, setMostrarPruebas] = useState(() => {
    try { return localStorage.getItem('limpack_transferencias_ver_pruebas') === '1' } catch { return false }
  })
  useEffect(() => {
    try { localStorage.setItem('limpack_transferencias_ver_pruebas', mostrarPruebas ? '1' : '0') } catch {}
  }, [mostrarPruebas])

  useEffect(() => { cargar() }, [])

  async function cargar() {
    setLoading(true)
    // Incluye:
    // • pendiente_pago (típico B2C que subió comprobante, espera confirmación)
    // • entregado con metodo=transferencia (B2B contra_entrega o a_plazo:
    //     entregamos, cobramos después con la transferencia bancaria)
    // Trae facturas relacionadas para filtrar los que YA se marcaron como cobrados
    // desde el panel Cobros B2B (aunque el pedido siga en estado='entregado').
    const { data, error } = await supabase
      .from('pedidos')
      .select(`
        id, numero_orden, comprador_nombre, comprador_email, comprador_telefono,
        comprador_profile_id, tipo, items, kit_codigo, estado,
        entrega_direccion, entrega_apto, entrega_dia_semana, entrega_ventana,
        monto_subtotal, costo_envio, monto_total, metodo_pago, pago_referencia,
        fecha_entrega_programada, entregado_at, created_at, empresa_id, es_prueba,
        comprobante_transferencia_url, comprobante_transferencia_uploaded_at,
        notas,
        barrio:barrios!pedidos_entrega_barrio_id_fkey(nombre),
        empresa:empresas!pedidos_empresa_id_fkey(id, razon_social, rut),
        facturas:facturas_emitidas(id, serie, numero, cobro_b2b_estado, cobro_b2b_referencia)
      `)
      .eq('metodo_pago', 'transferencia')
      .in('estado', ['pendiente_pago', 'entregado'])
      .order('created_at', { ascending: true })

    if (error) console.error('cargar pendientes:', error)
    // Excluir pedidos que ya tienen al menos una factura marcada como cobrada
    // (los cobrás desde Cobros B2B → no vuelven a aparecer acá).
    const pedidosPendientes = (data || []).filter(p => {
      const facts = Array.isArray(p.facturas) ? p.facturas : []
      if (facts.length === 0) return true
      const hayCobrada = facts.some(f => f.cobro_b2b_estado === 'cobrado')
      return !hayCobrada
    })
    setPedidos(pedidosPendientes)
    setLoading(false)
  }

  async function confirmarPago(p) {
    if (!confirm(`¿Confirmar pago de ${fmt(p.monto_total)} para ${p.comprador_nombre} (${p.numero_orden})?\n\nEsto va a:\n• Marcar el pedido como pagado\n• Disparar emisión automática del CFE\n• Enviar email + WhatsApp al cliente`)) return
    setConfirmando(p.id)
    try {
      const { error } = await supabase
        .from('pedidos')
        .update({
          estado: 'pagado',
          pagado_at: new Date().toISOString(),
          pagado_confirmado_por_id: user?.id,
        })
        .eq('id', p.id)
      if (error) throw error
      await cargar()
    } catch (e) {
      alert('Error al confirmar: ' + e.message)
    }
    setConfirmando(null)
  }

  async function verComprobante(p) {
    if (!p.comprobante_transferencia_url) return
    const { data, error } = await supabase.storage
      .from('comprobantes-transferencia')
      .createSignedUrl(p.comprobante_transferencia_url, 300)
    if (error) { alert('No se pudo abrir el comprobante: ' + error.message); return }
    setComprobantePreview({ url: data.signedUrl, filename: p.comprobante_transferencia_url.split('/').pop() })
  }

  const pedidosSinPruebas = pedidos.filter(p => mostrarPruebas || !p.es_prueba)
  const totalPruebas = pedidos.filter(p => p.es_prueba).length

  const pedidosFiltrados = pedidosSinPruebas.filter(p => {
    if (filtro === 'todos') return true
    if (filtro === 'b2b') return !!p.empresa_id
    if (filtro === 'b2c') return !p.empresa_id
    return true
  })

  const totalPendiente = pedidosFiltrados.reduce((s, p) => s + Number(p.monto_total || 0), 0)
  const sinComprobante = pedidosFiltrados.filter(p => !p.comprobante_transferencia_url).length

  const countB2C = pedidosSinPruebas.filter(p => !p.empresa_id).length
  const countB2B = pedidosSinPruebas.filter(p => !!p.empresa_id).length

  return (
    <AdminLayout>
      <div className="cpt-wrap">
        <div className="cpt-headline">
          <h1>🏦 Cobros pendientes · transferencia</h1>
          <p>Pedidos pagados por transferencia bancaria que esperan confirmación. Una vez confirmado, dispara CFE + email + WhatsApp automáticos.</p>
        </div>

        <div className="cpt-kpis">
          <div className="cpt-kpi">
            <div className="cpt-kpi-label">Pendientes</div>
            <div className="cpt-kpi-num" style={{ color: pedidosFiltrados.length > 0 ? '#f59e0b' : '#94a3b8' }}>{pedidosFiltrados.length}</div>
            <div className="cpt-kpi-sub">{pedidosFiltrados.length === 1 ? 'pedido' : 'pedidos'}</div>
          </div>
          <div className="cpt-kpi">
            <div className="cpt-kpi-label">Monto pendiente</div>
            <div className="cpt-kpi-num">{fmt(totalPendiente)}</div>
            <div className="cpt-kpi-sub">a recibir</div>
          </div>
          <div className="cpt-kpi">
            <div className="cpt-kpi-label">Con comprobante</div>
            <div className="cpt-kpi-num" style={{ color: pedidosFiltrados.length - sinComprobante > 0 ? '#16a34a' : '#94a3b8' }}>
              {pedidosFiltrados.length - sinComprobante}
            </div>
            <div className="cpt-kpi-sub">de {pedidosFiltrados.length}</div>
          </div>
        </div>

        {/* Tabs B2C / B2B */}
        <div className="cpt-tabs">
          <button className={`cpt-tab ${filtro === 'todos' ? 'active' : ''}`} onClick={() => setFiltro('todos')}>
            Todos <span className="cpt-tab-count">{pedidosSinPruebas.length}</span>
          </button>
          <button className={`cpt-tab ${filtro === 'b2c' ? 'active' : ''}`} onClick={() => setFiltro('b2c')}>
            🛒 B2C <span className="cpt-tab-count">{countB2C}</span>
          </button>
          <button className={`cpt-tab ${filtro === 'b2b' ? 'active' : ''}`} onClick={() => setFiltro('b2b')}>
            🏢 B2B <span className="cpt-tab-count">{countB2B}</span>
          </button>
          {totalPruebas > 0 && (
            <button
              type="button"
              onClick={() => setMostrarPruebas(v => !v)}
              title={mostrarPruebas ? 'Ocultar pedidos marcados como prueba' : 'Mostrar pedidos marcados como prueba'}
              style={{
                marginLeft: 'auto', padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                background: mostrarPruebas ? '#FEF3C7' : '#F1F5F9',
                color: mostrarPruebas ? '#78350F' : '#475569',
                border: `1px solid ${mostrarPruebas ? '#FCD34D' : '#CBD5E1'}`,
              }}
            >
              🧪 {mostrarPruebas ? `Ocultar pruebas (${totalPruebas})` : `Ver pruebas (${totalPruebas})`}
            </button>
          )}
        </div>

        {loading ? (
          <div className="cpt-loading">Cargando…</div>
        ) : pedidosFiltrados.length === 0 ? (
          <div className="cpt-empty">
            <div className="cpt-empty-icon">✓</div>
            <h3>Sin pendientes</h3>
            <p>No hay pedidos pagados por transferencia esperando confirmación{filtro !== 'todos' ? ` en ${filtro.toUpperCase()}` : ''}.</p>
            <p style={{ fontSize: 12, marginTop: 8 }}>
              <Link to="/admin/clientes" style={{ color: '#1A3461', fontWeight: 700 }}>Ver clientes habilitados →</Link>
            </p>
          </div>
        ) : (
          <div className="cpt-list">
            {pedidosFiltrados.map(p => {
              const itemsCount = Array.isArray(p.items) ? p.items.length : 0
              const tieneComprobante = !!p.comprobante_transferencia_url
              const diasEspera = Math.floor((Date.now() - new Date(p.created_at).getTime()) / 86400000)
              const esB2B = !!p.empresa_id
              return (
                <div key={p.id} className="cpt-card">
                  <div className="cpt-card-head">
                    <div className="cpt-card-num">
                      <strong>{p.numero_orden}</strong>
                      <span className={`cpt-pill ${esB2B ? 'cpt-pill-b2b' : 'cpt-pill-b2c'}`}>
                        {esB2B ? '🏢 B2B' : '🛒 B2C'}
                      </span>
                      <span className="cpt-card-tipo">{p.tipo === 'suscripcion' ? '🔄 Suscripción' : '🛒 Compra única'}</span>
                      {diasEspera > 3 && (
                        <span className="cpt-card-warn">⚠ {diasEspera}d esperando</span>
                      )}
                    </div>
                    <div className="cpt-card-monto">{fmt(p.monto_total)}</div>
                  </div>

                  <div className="cpt-card-body">
                    <div className="cpt-card-col">
                      {esB2B && p.empresa && (
                        <div className="cpt-card-row" style={{ background: '#fef3c7', padding: 6, borderRadius: 6, marginBottom: 4 }}>
                          <span className="cpt-label">Empresa</span>
                          <strong>{p.empresa.razon_social} {p.empresa.rut ? `· RUT ${p.empresa.rut}` : ''}</strong>
                        </div>
                      )}
                      <div className="cpt-card-row">
                        <span className="cpt-label">{esB2B ? 'Contacto' : 'Comprador'}</span>
                        <strong>{p.comprador_nombre || '—'}</strong>
                      </div>
                      {p.comprador_email && <div className="cpt-card-row"><span className="cpt-label">Email</span>{p.comprador_email}</div>}
                      {p.comprador_telefono && <div className="cpt-card-row"><span className="cpt-label">Tel</span>{p.comprador_telefono}</div>}
                      <div className="cpt-card-row">
                        <span className="cpt-label">Referencia</span>
                        <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: 4, fontSize: 11 }}>{p.pago_referencia || p.numero_orden}</code>
                      </div>
                    </div>
                    <div className="cpt-card-col">
                      <div className="cpt-card-row"><span className="cpt-label">Entrega</span>{p.entrega_direccion}{p.entrega_apto ? ` ap ${p.entrega_apto}` : ''}</div>
                      <div className="cpt-card-row"><span className="cpt-label">Barrio</span>{p.barrio?.nombre || '—'}</div>
                      <div className="cpt-card-row"><span className="cpt-label">Día</span>{p.entrega_dia_semana || '—'} · {p.entrega_ventana || '—'}</div>
                      <div className="cpt-card-row"><span className="cpt-label">Fecha entrega</span>{fmtFecha(p.fecha_entrega_programada)}</div>
                      <div className="cpt-card-row"><span className="cpt-label">Items</span>{itemsCount} {p.kit_codigo ? `(kit ${p.kit_codigo})` : ''}</div>
                    </div>
                  </div>

                  <div className="cpt-card-foot">
                    <div className="cpt-comprobante">
                      {tieneComprobante ? (
                        <button className="cpt-btn-ghost" onClick={() => verComprobante(p)}>
                          📄 Ver comprobante ({fmtFechaHora(p.comprobante_transferencia_uploaded_at)})
                        </button>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: 12 }}>⚠ Sin comprobante subido</span>
                      )}
                    </div>
                    <div className="cpt-card-actions">
                      {p.comprador_telefono && (() => {
                        const tel = p.comprador_telefono.replace(/\D/g, '')
                        const e164 = tel.startsWith('598') ? tel : tel.startsWith('0') ? '598' + tel.slice(1) : '598' + tel
                        const nombre = (p.comprador_nombre || '').split(' ')[0] || 'Hola'
                        const monto = Math.round(Number(p.monto_total || 0)).toLocaleString('es-UY')
                        const msg = `Hola ${nombre}! Te escribimos de Limpack. Estamos esperando la confirmación de tu transferencia por $${monto} del pedido ${p.numero_orden}. ¿Podrías reenviarnos el comprobante o decirnos desde qué cuenta la hiciste para identificarla? Gracias!`
                        return (
                          <a
                            className="cpt-btn-ghost"
                            href={`https://wa.me/${e164}?text=${encodeURIComponent(msg)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Abrir WhatsApp con mensaje pre-armado">
                            💬 WhatsApp
                          </a>
                        )
                      })()}
                      <button
                        className="cpt-btn-primary"
                        disabled={confirmando === p.id}
                        onClick={() => confirmarPago(p)}
                      >
                        {confirmando === p.id ? 'Confirmando…' : '✓ Confirmar pago recibido'}
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {comprobantePreview && (
          <div className="cpt-preview-bg" onClick={() => setComprobantePreview(null)}>
            <div className="cpt-preview-card" onClick={e => e.stopPropagation()}>
              <div className="cpt-preview-head">
                <strong>{comprobantePreview.filename}</strong>
                <button className="cpt-btn-ghost" onClick={() => setComprobantePreview(null)}>✕</button>
              </div>
              <div className="cpt-preview-body">
                {/\.(jpe?g|png|webp)$/i.test(comprobantePreview.filename) ? (
                  <img src={comprobantePreview.url} alt="" style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8 }} />
                ) : (
                  <iframe src={comprobantePreview.url} style={{ width: '100%', height: '70vh', border: 0, borderRadius: 8 }} title="Comprobante" />
                )}
              </div>
              <div className="cpt-preview-foot">
                <a href={comprobantePreview.url} target="_blank" rel="noreferrer" className="cpt-btn-ghost">Abrir en pestaña ↗</a>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .cpt-wrap { max-width: 1100px; margin: 0 auto; padding: 16px }
        .cpt-headline h1 { font-size: 22px; font-weight: 900; color: #1A3461; letter-spacing: -.01em }
        .cpt-headline p { font-size: 13px; color: #64748b; margin-top: 4px; line-height: 1.5 }

        .cpt-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 10px; margin: 18px 0 20px }
        .cpt-kpi { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px 16px }
        .cpt-kpi-label { font-size: 10px; font-weight: 800; color: #64748b; text-transform: uppercase; letter-spacing: .06em }
        .cpt-kpi-num { font-size: 24px; font-weight: 900; color: #1A3461; margin-top: 2px; letter-spacing: -.02em }
        .cpt-kpi-sub { font-size: 11px; color: #94a3b8; margin-top: 2px }

        .cpt-tabs { display: flex; gap: 4px; border-bottom: 1px solid #e2e8f0; margin: 18px 0 14px; overflow-x: auto }
        .cpt-tab { background: transparent; border: 0; padding: 10px 14px; cursor: pointer; font-family: inherit; font-size: 13px; font-weight: 700; color: #94a3b8; border-bottom: 2px solid transparent; display: inline-flex; align-items: center; gap: 6px; flex-shrink: 0 }
        .cpt-tab.active { color: #1A3461; border-bottom-color: #00A8B5 }
        .cpt-tab-count { font-size: 11px; opacity: .7; font-weight: 800 }

        .cpt-pill { font-size: 10px; font-weight: 800; padding: 2px 8px; border-radius: 999px; text-transform: uppercase; letter-spacing: .04em }
        .cpt-pill-b2c { background: #dbeafe; color: #1e40af }
        .cpt-pill-b2b { background: #fef3c7; color: #92400e }

        .cpt-loading { padding: 40px; text-align: center; color: #94a3b8 }
        .cpt-empty { background: #f8fafc; border: 1px dashed #cbd5e1; border-radius: 14px; padding: 50px 24px; text-align: center; margin-top: 10px }
        .cpt-empty-icon { font-size: 56px; color: #16a34a; margin-bottom: 10px }
        .cpt-empty h3 { font-size: 18px; font-weight: 800; color: #1A3461 }
        .cpt-empty p { font-size: 13px; color: #64748b; margin-top: 6px }

        .cpt-list { display: flex; flex-direction: column; gap: 12px }
        .cpt-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; overflow: hidden }
        .cpt-card-head { display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; border-bottom: 1px solid #f1f5f9; background: linear-gradient(180deg, #f8fafc, #fff) }
        .cpt-card-num { display: flex; gap: 8px; align-items: center; flex-wrap: wrap }
        .cpt-card-num strong { color: #1a3461; font-size: 14px }
        .cpt-card-tipo { font-size: 12px; color: #64748b; font-weight: 700 }
        .cpt-card-warn { font-size: 11px; font-weight: 800; padding: 2px 8px; border-radius: 999px; background: #fef3c7; color: #92400e }
        .cpt-card-monto { font-size: 18px; font-weight: 900; color: #1A3461 }

        .cpt-card-body { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; padding: 14px 16px }
        @media (max-width: 600px) { .cpt-card-body { grid-template-columns: 1fr } }
        .cpt-card-col { display: flex; flex-direction: column; gap: 4px }
        .cpt-card-row { font-size: 13px; color: #1a3461; display: flex; gap: 10px; align-items: baseline; line-height: 1.5 }
        .cpt-label { font-size: 10px; font-weight: 800; color: #94a3b8; text-transform: uppercase; letter-spacing: .04em; min-width: 80px }

        .cpt-card-foot { display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; border-top: 1px solid #f1f5f9; background: #f8fafc; flex-wrap: wrap; gap: 8px }
        .cpt-comprobante { flex: 1; min-width: 200px }

        .cpt-btn-primary { background: #16a34a; color: #fff; border: 0; padding: 9px 18px; border-radius: 8px; font-size: 13px; font-weight: 800; cursor: pointer; font-family: inherit; transition: background .15s }
        .cpt-btn-primary:hover { background: #15803d }
        .cpt-btn-primary:disabled { background: #94a3b8; cursor: not-allowed }
        .cpt-btn-ghost { background: transparent; border: 1px solid #cbd5e1; color: #475569; padding: 6px 12px; border-radius: 6px; font-size: 12px; font-weight: 700; cursor: pointer; font-family: inherit; text-decoration: none; display: inline-block }
        .cpt-btn-ghost:hover { background: #f1f5f9 }

        .cpt-preview-bg { position: fixed; inset: 0; background: rgba(15,31,61,.7); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 20px }
        .cpt-preview-card { background: #fff; border-radius: 14px; max-width: 800px; width: 100%; max-height: 90vh; overflow: auto; padding: 16px }
        .cpt-preview-head { display: flex; justify-content: space-between; align-items: center; padding-bottom: 10px; border-bottom: 1px solid #e2e8f0 }
        .cpt-preview-body { padding: 14px 0; text-align: center }
        .cpt-preview-foot { padding-top: 10px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end }
      `}</style>
    </AdminLayout>
  )
}
